import React from 'react';
import { TextField } from '@mui/material';
import { Button } from 'components/index';
import Card from '../general/Card';
import { format } from 'common/utils/common.util';
import { CCReport } from './construction-cost-report.type';
import { useFormik } from 'formik';
import * as yup from 'yup';
import WhiteFab from '../common/WhiteFab';
import { Close, Edit } from '@mui/icons-material';
import NumericFormatInput from '../general/NumericFormatInput';

interface CCReportInputsCardProps {
  report: CCReport;
  updateReport: (updatedReport: CCReport) => void;
}

const CCReportInputsCard: React.FC<CCReportInputsCardProps> = ({ report, updateReport }) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const toggleEditMode = () => setIsEditMode(val => !val);

  React.useEffect(() => {
    setIsEditMode(false);
  }, [report]);

  const initialOrDefaultValues: Partial<CCReport> = {
    grossBuildingSF: report.grossBuildingSF,
    netRentableSF: report.netRentableSF,
    unitCount: report.unitCount,
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      grossBuildingSF: yup.number().nullable().min(0, 'Must not be negative'),
      netRentableSF: yup.number().nullable().min(0, 'Must not be negative'),
      unitCount: yup.number().nullable().min(0, 'Must not be negative'),
    }),
    onSubmit: values => {
      updateReport({
        ...report,
        grossBuildingSF: values.grossBuildingSF,
        netRentableSF: values.netRentableSF,
        unitCount: values.unitCount,
      });
      setIsEditMode(false);
    },
  });

  const canSubmit = React.useMemo(() => {
    if (!formik.isValid) return false;
    return formik.dirty;
  }, [formik]);

  const handleCancel = () => {
    formik.resetForm();
    setIsEditMode(false);
  };

  return (
    <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
      <Card
        heading='Inputs'
        itemsPerRow={3}
        headerAction={
          isEditMode ? (
            <WhiteFab aria-label='cancel' icon={<Close fontSize='small' />} textColor='error' size='small' onClick={handleCancel} />
          ) : (
            <WhiteFab aria-label='edit' icon={<Edit fontSize='small' />} textColor='primary' size='small' onClick={toggleEditMode} />
          )
        }
        footer={
          isEditMode ? (
            <Button disabled={!canSubmit} onClick={formik.submitForm} sx={{ marginLeft: 'auto' }}>
              Save Draft
            </Button>
          ) : undefined
        }
        data={
          isEditMode
            ? {
                'Gross SF': (
                  <TextField
                    name='grossBuildingSF'
                    variant='standard'
                    value={formik.values.grossBuildingSF}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.grossBuildingSF && Boolean(formik.errors.grossBuildingSF)}
                    helperText={formik.touched.grossBuildingSF && formik.errors.grossBuildingSF}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                  />
                ),
                Units: (
                  <TextField
                    name='unitCount'
                    variant='standard'
                    value={formik.values.unitCount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.unitCount && Boolean(formik.errors.unitCount)}
                    helperText={formik.touched.unitCount && formik.errors.unitCount}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                  />
                ),
                'Net SF': (
                  <TextField
                    name='netRentableSF'
                    variant='standard'
                    value={formik.values.netRentableSF}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.netRentableSF && Boolean(formik.errors.netRentableSF)}
                    helperText={formik.touched.netRentableSF && formik.errors.netRentableSF}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                  />
                ),
              }
            : {
                'Gross SF': format(report.grossBuildingSF?.toString() || '--', 'number'),
                Units: format(report.unitCount?.toString() || '--', 'number'),
                'Net SF': format(report.netRentableSF?.toString() || '--', 'number'),
              }
        }
      />
    </form>
  );
};

export default CCReportInputsCard;
